import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Checkbox, IconButton, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { Column } from "components/templates/dataGridTable/serverSide/types";
import { useGetRoleNameAndDescriptionQuery } from "graphqlBase/Role/__generated__/getRoleNameAndDescription";
import {
  GetUsersForRoleMappingQuery,
  PaginatedResultType,
} from "graphqlBase/Settings/__generated__/getUsersForRoleMapping";
import React, { useEffect, useMemo } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { drawerAtom, mappingRoleUserSelector, mappingRoleUsersLoading } from "../RecoilState/mappingUserRoleState";

export type User = PaginatedResultType<GetUsersForRoleMappingQuery["users"]>;

const iconButton = {
  backgroundColor: "transparent",
  "&:hover": { backgroundColor: "transparent" },
  "& svg": {
    height: "1em",
    width: "1em",
    "&:active": { backgroundColor: "transparent" },
  },
  width: "2em",
  height: "2em",
  right: 0,
  top: 0,
};

const roleTitleStyles = { display: "block", fontWeight: "700", fontSize: 14 };

const RoleCheckbox = ({ roleId, params }: { params: GridRenderCellParams<string, User>; roleId: string }) => {
  const userId = params?.row?.id;
  const state = mappingRoleUserSelector({ roleId, userId });
  const [checkedState, setCheckedState] = useRecoilState(state);
  const handleChange = () => {
    setCheckedState((prev) => !prev);
  };

  return <Checkbox checked={checkedState} onChange={handleChange} />;
};

const useGetUserRoleColumns = (users: User[] | undefined | null) => {
  const setDrawerState = useSetRecoilState(drawerAtom);
  const { data: rolesData, loading: loadingRoles } = useGetRoleNameAndDescriptionQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    onError: (queryError) => {
      console.error(queryError);
    },
  });

  const setRoleUsersLoading = useSetRecoilState(mappingRoleUsersLoading);

  useEffect(() => {
    setRoleUsersLoading(loadingRoles);
  }, [loadingRoles, setRoleUsersLoading]);

  const roles = useMemo(() => {
    if (!rolesData || !users) return [];

    return (rolesData.roles?.items ?? []).reduce<Array<Column<User, "User">>>((columns, current) => {
      if (!current) return columns;

      const column = {
        field: current?.name ?? "",
        minWidth: 150,
        maxWidth: 350,
        resizable: true,
        headerAlign: "center",
        align: "center",
        // eslint-disable-next-line react/display-name
        renderHeader: () => (
          <Typography sx={roleTitleStyles}>
            {current?.name}
            <IconButton
              sx={iconButton}
              size="small"
              onClick={() => setDrawerState({ id: current.id, name: current?.name, description: current?.description })}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </Typography>
        ),
        type: "string",
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams<string, User>) => (
          <RoleCheckbox roleId={current.id} params={params} />
        ),
      };

      // @ts-ignore - Dynamic role field names are expected by table config.
      return columns.concat(column);
    }, []);
  }, [rolesData, setDrawerState, users]);

  return { roles };
};

export default useGetUserRoleColumns;
